import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const Header = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [programasOpen, setProgramasOpen] = useState(false);
  const [serviciosOpen, setServiciosOpen] = useState(false);

  const handleNavigateWithScroll = (path, hash) => {
    navigate(path);
    setTimeout(() => {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    setPerfilOpen(false);
    setProgramasOpen(false);
    setServiciosOpen(false);
  };

  const perfilOptions = [
    { name: 'Yucatán en Cifras', path: '/perfil-yucatan', hash: '#yucatan-cifras' },
    { name: 'Localización', path: '/perfil-yucatan', hash: '#localizacion' },
    { name: 'Población', path: '/perfil-yucatan', hash: '#poblacion' },
    { name: 'Perfil Económico', path: '/perfil-yucatan', hash: '#perfil-economico' }
  ];

  const programasOptions = [
    { name: 'Yucatán sin Fronteras', path: '/programas' },
    { name: 'Yucatán en México', path: '/programas' },
    { name: 'Yucatán en el Mundo', path: '/programas' },
    { name: 'Impulsa tu Crecimiento', path: '/programas' },
    { name: 'Profesionistas de Cooperativa', path: '/programas' }
  ];

  const serviciosOptions = [
    { name: 'Portal de Trámites', path: '/servicios', hash: '#tramites' },
    { name: 'Financiamiento', path: '/servicios', hash: '#financiamiento' },
    { name: 'Estrategia de Transformación Digital', path: '/servicios', hash: '#transformacion-digital' },
    { name: 'Fomento a la Inversión', path: '/servicios', hash: '#fomento-inversion' },
    { name: 'Comercialización', path: '/servicios', hash: '#comercializacion' }
  ];

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex justify-end items-center text-xs text-gray-600">
            <span>Gobierno del Estado de Yucatán 2024-2030</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 transition-opacity hover:opacity-80">
            <img 
              src="https://customer-assets.emergentagent.com/job_9921f030-6fc5-40ce-b0b0-197018c0336f/artifacts/nk4ggdbk_Logo.png" 
              alt="Renacimiento Maya Yucatán" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <Link to="/" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all">
              Inicio
            </Link>
            
            <Link to="/bolsa-trabajo" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all">
              Bolsa de Trabajo
            </Link>
            
            <Link to="/noticias" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all">
              Noticias
            </Link>

            {/* Perfil de Yucatán Dropdown */}
            <div className="relative group">
              <button
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all flex items-center gap-1"
                onMouseEnter={() => setPerfilOpen(true)}
                onMouseLeave={() => setPerfilOpen(false)}
              >
                Perfil de Yucatán
                <ChevronDown className="h-4 w-4" />
              </button>
              {perfilOpen && (
                <div 
                  className="absolute left-0 mt-0 w-64 bg-white rounded-md shadow-lg border border-gray-200 py-2"
                  onMouseEnter={() => setPerfilOpen(true)}
                  onMouseLeave={() => setPerfilOpen(false)}
                >
                  {perfilOptions.map((option) => (
                    <button
                      key={option.name}
                      onClick={() => handleNavigateWithScroll(option.path, option.hash)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Programas Dropdown */}
            <div className="relative group">
              <button
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all flex items-center gap-1"
                onMouseEnter={() => setProgramasOpen(true)}
                onMouseLeave={() => setProgramasOpen(false)}
              >
                Programas
                <ChevronDown className="h-4 w-4" />
              </button>
              {programasOpen && (
                <div 
                  className="absolute left-0 mt-0 w-72 bg-white rounded-md shadow-lg border border-gray-200 py-2"
                  onMouseEnter={() => setProgramasOpen(true)}
                  onMouseLeave={() => setProgramasOpen(false)}
                >
                  {programasOptions.map((option) => (
                    <Link
                      key={option.name}
                      to={option.path}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      {option.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Servicios Dropdown */}
            <div className="relative group">
              <button
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all flex items-center gap-1"
                onMouseEnter={() => setServiciosOpen(true)}
                onMouseLeave={() => setServiciosOpen(false)}
              >
                Servicios
                <ChevronDown className="h-4 w-4" />
              </button>
              {serviciosOpen && (
                <div 
                  className="absolute left-0 mt-0 w-80 bg-white rounded-md shadow-lg border border-gray-200 py-2"
                  onMouseEnter={() => setServiciosOpen(true)}
                  onMouseLeave={() => setServiciosOpen(false)}
                >
                  {serviciosOptions.map((option) => (
                    <button
                      key={option.name}
                      onClick={() => handleNavigateWithScroll(option.path, option.hash)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to="/invierte-yucatan" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all">
              Invierte Yucatán
            </Link>
            
            <Link to="/normatividad" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all">
              Normatividad SETY
            </Link>
            
            <Link to="/contacto" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all">
              Contáctanos
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4 space-y-2">
            <Link to="/" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
              Inicio
            </Link>
            <Link to="/bolsa-trabajo" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
              Bolsa de Trabajo
            </Link>
            <Link to="/noticias" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
              Noticias
            </Link>
            <Link to="/programas" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
              Programas
            </Link>
            <Link to="/contacto" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
              Contáctanos
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;